import React, { useEffect, useMemo, useState } from "react";
import { adminList, adminCreate, adminUpdate, adminDelete } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Sparkles } from "lucide-react";

// field: { key, label, type: text|textarea|number|select|bool|list, options?, full? }
export default function CrudManager({ collection, title, fields, defaults = {}, filter, aiGenerate, intro }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // object or null
  const [form, setForm] = useState({});
  const [genOpen, setGenOpen] = useState(false);
  const [genTopic, setGenTopic] = useState("");
  const [genAffiliate, setGenAffiliate] = useState(true);
  const [genBusy, setGenBusy] = useState(false);

  const load = () => {
    setLoading(true);
    adminList(collection).then((d) => {
      setItems(filter ? d.filter(filter) : d);
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, [collection]); // eslint-disable-line

  const openNew = () => { setForm({ ...defaults }); setEditing({}); };
  const openEdit = (item) => {
    const f = { ...item };
    fields.forEach((fl) => {
      if (fl.type === "list" && Array.isArray(f[fl.key])) f[fl.key] = f[fl.key].join(", ");
      if (fl.type === "json" && f[fl.key] !== undefined && f[fl.key] !== null && typeof f[fl.key] !== "string") {
        f[fl.key] = JSON.stringify(f[fl.key], null, 2);
      }
    });
    setForm(f);
    setEditing(item);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    let jsonError = null;
    fields.forEach((fl) => {
      if (fl.type === "list") payload[fl.key] = (payload[fl.key] || "").split(",").map((s) => s.trim()).filter(Boolean);
      if (fl.type === "number") payload[fl.key] = Number(payload[fl.key] || 0);
      if (fl.type === "bool") payload[fl.key] = !!payload[fl.key];
      if (fl.type === "json") {
        const raw = (payload[fl.key] ?? "").toString().trim();
        if (!raw) { payload[fl.key] = null; return; }
        try { payload[fl.key] = JSON.parse(raw); }
        catch { jsonError = fl.label; }
      }
    });
    if (jsonError) {
      toast.error(`Invalid JSON in "${jsonError}" — check syntax.`);
      return;
    }
    try {
      if (editing && editing.id) await adminUpdate(collection, editing.id, payload);
      else await adminCreate(collection, payload);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch {
      toast.error("Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try { await adminDelete(collection, id); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const titleField = fields[0].key;

  const runGenerate = async () => {
    if (!genTopic.trim()) return;
    setGenBusy(true);
    try {
      const data = await aiGenerate(genTopic.trim(), genAffiliate);
      const f = { ...defaults, ...data };
      fields.forEach((fl) => { if (fl.type === "list" && Array.isArray(f[fl.key])) f[fl.key] = f[fl.key].join(", "); });
      setForm(f);
      setEditing({});
      setGenOpen(false);
      setGenTopic("");
      toast.success("Draft generated — review & save");
    } catch (e) {
      toast.error("Generation failed. Try again.");
    } finally {
      setGenBusy(false);
    }
  };

  return (
    <div data-testid={`crud-${collection}`}>
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-display text-3xl">{title}</h2>
        <div className="flex gap-3">
          {aiGenerate && (
            <button data-testid={`crud-ai-${collection}`} onClick={() => setGenOpen(true)} className="inline-flex items-center gap-2 border border-primary text-primary px-5 py-2.5 font-semibold hover:bg-primary hover:text-white active:scale-95 transition">
              <Sparkles className="w-4 h-4" /> AI Write
            </button>
          )}
          <button data-testid={`crud-new-${collection}`} onClick={openNew} className="inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 font-semibold hover:bg-primary/90 active:scale-95 transition">
            <Plus className="w-4 h-4" /> New
          </button>
        </div>
      </div>

      {intro && (
        <div data-testid={`crud-intro-${collection}`} className="p-5 mb-8 bg-primary/10 border border-primary/30 text-sm text-foreground/80 leading-relaxed">
          {intro}
        </div>
      )}

      {genOpen && (
        <div className="fixed inset-0 z-[300] bg-black/70 flex items-center justify-center p-4">
          <div className="bg-card border border-border w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-2xl flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" /> AI Article Writer</h3>
              <button onClick={() => setGenOpen(false)} className="p-1 hover:text-primary"><X className="w-6 h-6" /></button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Describe the article topic. The AI writes a full draft you can review and edit before publishing.</p>
            <textarea data-testid="ai-topic" rows={3} value={genTopic} onChange={(e) => setGenTopic(e.target.value)} placeholder="e.g. How small businesses can use AI to save time" className="w-full bg-secondary px-3 py-2.5 outline-none focus:ring-1 focus:ring-primary resize-none mb-4" />
            <label className="flex items-center gap-2 mb-4 cursor-pointer text-sm">
              <input data-testid="ai-affiliate-toggle" type="checkbox" checked={genAffiliate} onChange={(e) => setGenAffiliate(e.target.checked)} className="w-4 h-4 accent-[hsl(var(--primary))]" />
              <span>Naturally weave in <b>affiliate links</b> from my Recommended list</span>
            </label>
            <button data-testid="ai-generate-btn" onClick={runGenerate} disabled={genBusy} className="w-full bg-primary text-white py-3 font-semibold hover:bg-primary/90 active:scale-95 transition disabled:opacity-60">
              {genBusy ? "Writing your article…" : "Generate Draft"}
            </button>
          </div>
        </div>
      )}

      {loading ? <p className="text-muted-foreground">Loading…</p> : (
        <div className="border border-border divide-y divide-border">
          {items.length === 0 && <p className="p-6 text-muted-foreground">No items yet.</p>}
          {items.map((item) => (
            <div key={item.id} className="flex items-center justify-between gap-4 p-4 hover:bg-secondary/40 transition-colors">
              <div className="flex items-center gap-4 min-w-0">
                {item.image && <img src={item.image} alt="" className="w-12 h-12 object-cover border border-border flex-shrink-0" />}
                <div className="min-w-0">
                  <p className="font-medium truncate">{item[titleField] || "Untitled"}</p>
                  <p className="text-xs text-muted-foreground truncate">{item.category || item.type || item.placement || ""}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={() => openEdit(item)} data-testid={`crud-edit-${item.id}`} className="p-2 border border-border hover:border-primary hover:text-primary transition"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => del(item.id)} data-testid={`crud-delete-${item.id}`} className="p-2 border border-border hover:border-destructive hover:text-destructive transition"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-[300] bg-black/70 flex items-center justify-center p-4 overflow-y-auto">
          <form onSubmit={save} className="bg-card border border-border w-full max-w-2xl my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-card">
              <h3 className="font-display text-2xl">{editing.id ? "Edit" : "New"} {title}</h3>
              <button type="button" onClick={() => setEditing(null)} className="p-1 hover:text-primary"><X className="w-6 h-6" /></button>
            </div>
            <div className="p-6 grid sm:grid-cols-2 gap-5">
              {fields.map((fl) => (
                <div key={fl.key} className={fl.full || fl.type === "textarea" || fl.type === "json" ? "sm:col-span-2" : ""}>
                  <label className="overline text-muted-foreground block mb-2">{fl.label}</label>
                  {fl.type === "textarea" ? (
                    <textarea rows={fl.rows || 4} value={form[fl.key] || ""} onChange={(e) => setForm((f) => ({ ...f, [fl.key]: e.target.value }))} className="w-full bg-secondary px-3 py-2.5 outline-none focus:ring-1 focus:ring-primary resize-y" />
                  ) : fl.type === "json" ? (
                    <textarea
                      rows={fl.rows || 8}
                      value={form[fl.key] || ""}
                      onChange={(e) => setForm((f) => ({ ...f, [fl.key]: e.target.value }))}
                      spellCheck={false}
                      placeholder={fl.placeholder || '{\n  "sizes": ["S", "M", "L"],\n  "colors": [{"name": "Black", "hex": "#000000"}]\n}'}
                      className="w-full bg-secondary px-3 py-2.5 outline-none focus:ring-1 focus:ring-primary resize-y font-mono text-xs"
                    />
                  ) : fl.type === "select" ? (
                    <select value={form[fl.key] || ""} onChange={(e) => setForm((f) => ({ ...f, [fl.key]: e.target.value }))} className="w-full bg-secondary px-3 py-2.5 outline-none focus:ring-1 focus:ring-primary">
                      <option value="">Select…</option>
                      {fl.options.map((o, oi) => <option key={`${oi}-${o}`} value={o}>{o}</option>)}
                    </select>
                  ) : fl.type === "bool" ? (
                    <label className="flex items-center gap-2 py-2 cursor-pointer">
                      <input type="checkbox" checked={!!form[fl.key]} onChange={(e) => setForm((f) => ({ ...f, [fl.key]: e.target.checked }))} className="w-4 h-4 accent-[hsl(var(--primary))]" />
                      <span className="text-sm text-muted-foreground">Enabled</span>
                    </label>
                  ) : (
                    <input type={fl.type === "number" ? "number" : "text"} step="any" value={form[fl.key] ?? ""} onChange={(e) => setForm((f) => ({ ...f, [fl.key]: e.target.value }))} placeholder={fl.type === "list" ? "comma, separated, values" : ""} className="w-full bg-secondary px-3 py-2.5 outline-none focus:ring-1 focus:ring-primary" />
                  )}
                  {fl.hint && <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{fl.hint}</p>}
                </div>
              ))}
            </div>
            <div className="p-6 border-t border-border flex justify-end gap-3 sticky bottom-0 bg-card">
              <button type="button" onClick={() => setEditing(null)} className="px-6 py-3 border border-border hover:border-primary transition">Cancel</button>
              <button type="submit" data-testid={`crud-save-${collection}`} className="px-6 py-3 bg-primary text-white font-semibold hover:bg-primary/90 active:scale-95 transition">Save</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
